import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Slider, IconButton } from '@mui/material';
import { Loader2, Pause, Play, SkipBack, SkipForward } from 'lucide-react';
import { RiForward10Fill, RiReplay10Fill } from 'react-icons/ri';
import { formatPlayerTime } from '@wajeez-web/core/utils';
import AudioContentSplitter from './AudioContentSplitter';
import { useTranslation } from 'react-i18next';
import { MediaHelpers } from '@wajeez-web/core/api';
import { Server } from '@wajeez/wajeez.microservices';

interface AudioChapterOutput {
  narratorId: number;
  from: number;
  to: number;
}
export interface Chapter {
  id?: number;
  title: string;
  text: string;
  point: number;
  fromIndex?: number;
  toIndex?: number;
  audioChapters?: AudioChapterOutput[];
}

interface AudioSegmentManagerProps {
  audioURL?: string | null;
  contentChapters: Server.ChapterOutput2[];
  originalFullText: Server.TextOutput[];
  contentData: Server.ContentEntityMultilingualOutput | null;
  languageId: number;
  type: Server.ContentEntityTypeDetailedOutput;
  isRtl: boolean;
  setContentData: React.Dispatch<
    React.SetStateAction<Server.EditContentEntityByIdV2Input | null>
  >;
}

const AudioSegmentManager = ({
  audioURL,
  contentChapters,
  originalFullText,
  // contentData,
  languageId,
  // type,
  isRtl,
  setContentData,
}: AudioSegmentManagerProps) => {
  const [audioFile, setAudioFile] = useState<string | null>(audioURL ?? null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [duration, setDuration] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPlayerTimeChange, setIsPlayerTimeChange] = useState<boolean>(false);
  const [fullText, setFullText] = useState<string>('');

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const sliderRef = useRef<HTMLDivElement | null>(null);

  const extractedChapters = (
    contentChapters: Server.ChapterOutput2[],
    originalFullText: Server.TextOutput[]
  ): Chapter[] => {
    return contentChapters.map((chapter) => {
      const chapterText = originalFullText[0].text?.slice(
        chapter.fromIndex,
        chapter.toIndex
      );

      const audioPoint = chapter.audioChapters?.[0]?.to ?? 0;

      return {
        id: chapter.id,
        title: chapter.title ?? '',
        text: chapterText ?? '',
        point: audioPoint * 100,
        fromIndex: chapter.fromIndex,
        toIndex: chapter.toIndex,
      };
    });
  };

  const step = () => {
    if (duration <= 30) {
      return 0.1; // 100ms steps for short audio (<30s)
    } else if (duration <= 120) {
      return 0.5; // 500ms steps for medium audio (30s-2min)
    } else if (duration <= 600) {
      return 1; // 1s steps for longer audio (2-10min)
    } else {
      return 2; // 2s steps for very long audio (>10min)
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      setIsLoading(true);
      const mediaId = await MediaHelpers.uploadLargeMedia({
        data: file,
        fileName: file.name,
      });

      if (mediaId) {
        setContentData((prev) => {
          if (!prev) return null;

          const updatedProfiles = prev.profiles?.map((p) => {
            if (p.languageId === languageId) {
              const resetAudioNarrator = (p.narratorAudios || []).map((n) =>
                n.isDefault ? { ...n, isDefault: false } : n
              );

              const updatedNarratorAudios = [
                ...resetAudioNarrator,
                {
                  narratorId: 0,
                  audioReference: mediaId,
                  isDefault: true,
                },
              ];

              return {
                ...p,
                narratorAudios: updatedNarratorAudios,
                chapters: [],
              };
            }
            return p;
          });

          return {
            ...prev,
            id: prev.id,
            originalTitle: prev.originalTitle,
            slug: prev.slug,
            originalSubTitle: prev.originalSubTitle,
            actualCost: prev.actualCost,
            actualDurationMS: prev.actualDurationMS,
            profiles: updatedProfiles || [],
          };
        });

        const mediaUrl = await MediaHelpers.getMediaUrlByIdAsync(mediaId);
        setAudioFile(mediaUrl);
      }
    } catch (error) {
      console.error('Error loading audio:', error);
      setAudioFile(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSkipBackward = () => {
    if (!audioRef.current) return;
    const newTime = audioRef.current.duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleForwardSkipInterval = (skipInterval: number) => {
    if (!audioRef.current) return;
    const newTime = Math.max(audioRef.current.currentTime - skipInterval, 0);
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleBackwardSkipInterval = (skipInterval: number) => {
    if (!audioRef.current) return;
    const newTime = Math.min(
      audioRef.current.currentTime + skipInterval,
      audioRef.current.duration
    );
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleSkipForward = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    if (!audioRef.current) return;
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    audioRef.current.currentTime = value;
    setCurrentTime(value);
    setIsPlayerTimeChange(!isPlayerTimeChange);
  };

  const handleTimeChange = (newTime: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const resturctuedChapters = (chapters: Chapter[]) => {
    return chapters.map(({ title, fromIndex, toIndex, audioChapters }) => {
      return {
        title,
        fromIndex,
        toIndex,
        audioChapters,
      };
    });
  };

  useEffect(() => {
    console.log({ chapters });

    setContentData((prev) => {
      if (!prev) return null;

      const updatedProfiles = prev.profiles?.map((p) => {
        if (p.languageId === languageId) {
          return {
            ...p,
            chapters: resturctuedChapters(chapters),
            texts: [{ text: fullText, isDefault: true }],
          };
        }
        return p;
      });

      return {
        ...prev,
        id: prev.id,
        originalTitle: prev.originalTitle,
        slug: prev.slug,
        originalSubTitle: prev.originalSubTitle,
        actualCost: prev.actualCost,
        actualDurationMS: prev.actualDurationMS,
        profiles: updatedProfiles || [],
      };
    });
  }, [chapters, fullText]);

  useEffect(() => {
    if (contentChapters.length) {
      const chapters = extractedChapters(contentChapters, originalFullText);
      setChapters(chapters);
    }
  }, [contentChapters]);

  useEffect(() => {
    if (audioURL) {
      setAudioFile(audioURL);
    }
  }, [audioURL]);

  useEffect(() => {
    if (!audioRef.current) return;

    const handleLoadedMetadata = () => setDuration(audioRef.current!.duration);
    const handleTimeUpdate = () =>
      setCurrentTime(audioRef.current!.currentTime);

    audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
    audioRef.current.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      audioRef.current?.removeEventListener(
        'loadedmetadata',
        handleLoadedMetadata
      );
      audioRef.current?.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [audioFile]);

  useEffect(() => {
    sliderRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [isPlayerTimeChange]);

  return (
    <div className="flex flex-col items-center w-full gap-5 min-h-[500px]">
      {!audioFile && (
        <div className="mx-auto">
          <UploadButton
            isLoading={isLoading}
            handleFileChange={handleFileChange}
          />
        </div>
      )}
      {audioFile && (
        <div className="p-5 pb-0 w-full">
          <div ref={sliderRef}>
            <audio ref={audioRef} src={audioFile} className="hidden" />
            <div className="flex justify-center items-center gap-5">
              <div className="text-[15px] font-medium">
                {formatPlayerTime(currentTime)}
              </div>
              <div className="flex justify-center w-full">
                <Slider
                  value={currentTime}
                  step={step()}
                  min={0}
                  max={duration}
                  onChange={handleSliderChange}
                />
              </div>
              <div className="text-[15px] font-medium">
                {formatPlayerTime(duration)}
              </div>
            </div>
            <div className="flex items-center">
              <div className="mr-auto">
                <AudioControls
                  isPlaying={isPlaying}
                  onPlayPause={handlePlayPause}
                  onSkipForward={handleSkipForward}
                  onSkipBackward={handleSkipBackward}
                  onForwardInterval={(seconds) =>
                    handleForwardSkipInterval(seconds)
                  }
                  onBackwardInterval={(seconds) =>
                    handleBackwardSkipInterval(seconds)
                  }
                  disabled={isLoading}
                />
              </div>
              <div className="mr-auto">
                <UploadButton
                  isLoading={isLoading}
                  handleFileChange={handleFileChange}
                />
              </div>
            </div>
            <div className="h-[2px] w-full bg-gray-400 my-3" />
          </div>
          <AudioContentSplitter
            currentTime={currentTime}
            setCurrentTime={handleTimeChange}
            duration={duration}
            chapters={chapters}
            setChapters={setChapters}
            isPlayerTimeChange={isPlayerTimeChange}
            originalFullText={originalFullText[0]?.text ?? ''}
            isRtl={isRtl}
            setFullText={setFullText}
          />
        </div>
      )}
    </div>
  );
};

export default AudioSegmentManager;

export const UploadButton = ({
  isLoading,
  handleFileChange,
}: {
  isLoading: boolean;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  const { t } = useTranslation();

  return (
    <label
      htmlFor="uploadFile"
      className={`flex justify-center items-center gap-2 bg-blue-400 text-white text-[16px] font-medium px-3 py-2 outline-none rounded w-max cursor-pointer ${
        isLoading && 'opacity-80 cursor-text'
      }`}
    >
      {isLoading && <Loader2 className="animate-spin" />}
      {t('buttons.Download')}
      {!isLoading && (
        <input
          type="file"
          id="uploadFile"
          className="hidden"
          accept="audio/*"
          onChange={handleFileChange}
        />
      )}
    </label>
  );
};

interface AudioControlsProps {
  isPlaying: boolean;
  onPlayPause: () => void;
  onSkipForward: () => void;
  onSkipBackward: () => void;
  onForwardInterval: (seconds: number) => void;
  onBackwardInterval: (seconds: number) => void;
  disabled?: boolean;
}

export const AudioControls = ({
  isPlaying,
  onPlayPause,
  onSkipForward,
  onSkipBackward,
  onForwardInterval,
  onBackwardInterval,
  disabled = false,
}: AudioControlsProps) => {
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case ' ':
          e.preventDefault();
          onPlayPause();
          break;
        case 'ArrowRight':
          e.preventDefault();
          onForwardInterval(10);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onBackwardInterval(10);
          break;
      }
    },
    [onPlayPause, onForwardInterval, onBackwardInterval]
  );

  return (
    <div
      className="flex justify-center items-center gap-5"
      onKeyDown={handleKeyPress}
      role="group"
      aria-label="Audio controls"
    >
      <IconButton
        onClick={onSkipForward}
        aria-label="Skip to end"
        disabled={disabled}
      >
        <SkipForward />
      </IconButton>
      <IconButton
        onClick={() => onForwardInterval(10)}
        aria-label="Forward 10 seconds"
        disabled={disabled}
      >
        <RiForward10Fill />
      </IconButton>
      <IconButton
        onClick={onPlayPause}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        disabled={disabled}
      >
        {isPlaying ? <Pause /> : <Play />}
      </IconButton>
      <IconButton
        onClick={() => onBackwardInterval(10)}
        aria-label="Rewind 10 seconds"
        disabled={disabled}
      >
        <RiReplay10Fill />
      </IconButton>
      <IconButton
        onClick={onSkipBackward}
        aria-label="Skip to start"
        disabled={disabled}
      >
        <SkipBack />
      </IconButton>
    </div>
  );
};
